import classNames from 'classnames';
import { Card } from 'react-bootstrap';
import Badge from '../../components/base/Badge';

const fmt = n => Number(n || 0).toLocaleString();
const safeArr = v => (Array.isArray(v) ? v : []);

const formatDate = iso => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const daysUntil = iso => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  const ms =
    d.setHours(0, 0, 0, 0) - new Date(now.setHours(0, 0, 0, 0)).getTime();
  return Math.round(ms / (1000 * 60 * 60 * 24));
};

const urgencyBadge = dueDate => {
  const diff = daysUntil(dueDate);
  if (diff == null) return { label: 'No due date', color: 'secondary' };
  if (diff < 0) return { label: `Overdue ${Math.abs(diff)}d`, color: 'danger' };
  if (diff === 0) return { label: 'Due today', color: 'warning' };
  if (diff <= 3) return { label: `Due in ${diff}d`, color: 'warning' };
  return { label: `Due in ${diff}d`, color: 'info' };
};

const productionBadgeFromLines = lines => {
  const total = lines.reduce(
    (s, l) => s + Number(l.totalOrderedLength || 0),
    0
  );
  const allocated = lines.reduce(
    (s, l) => s + Number(l.allocatedLength || 0),
    0
  );
  const produced = lines.reduce((s, l) => s + Number(l.producedLength || 0), 0);

  if (produced > 0) return { label: 'In progress', color: 'info' };
  if (allocated >= total && total > 0)
    return { label: 'Ready', color: 'success' };
  if (allocated > 0) return { label: 'Allocated', color: 'warning' };
  return { label: 'Not started', color: 'secondary' };
};

const OrderDetailsSummaryCard = ({ order, className }) => {
  const lines = safeArr(order?.lines);

  const totals = lines.reduce(
    (acc, l) => {
      acc.ordered += Number(l.totalOrderedLength || 0);
      acc.allocated += Number(l.allocatedLength || 0);
      acc.produced += Number(l.producedLength || 0);
      acc.remaining += Number(l.remainingLength || 0);
      acc.jobs += safeArr(l?.allocation?.allocations).length;
      return acc;
    },
    { ordered: 0, allocated: 0, produced: 0, remaining: 0, jobs: 0 }
  );

  const progress =
    totals.ordered > 0
      ? Math.min(100, Math.round((totals.produced / totals.ordered) * 100))
      : 0;

  const due = order?.dueDate || null;
  const urg = urgencyBadge(due);
  const prod = productionBadgeFromLines(lines);

  return (
    <Card className={classNames(className)}>
      <Card.Body>
        <Card.Title as="h3" className="mb-3">
          Summary
        </Card.Title>

        <div className="d-flex gap-2 flex-wrap mb-3">
          <Badge bg={prod.color} className="fs-10 rounded-pill py-2 px-3">
            {prod.label}
          </Badge>
          <Badge bg={urg.color} className="fs-10 rounded-pill py-2 px-3">
            {urg.label}
          </Badge>
          <Badge bg="secondary" className="fs-10 rounded-pill py-2 px-3">
            {fmt(totals.jobs)} job(s)
          </Badge>
        </div>

        <div className="border-bottom border-dashed mb-3 pb-3">
          <div className="d-flex justify-content-between mb-2">
            <span className="text-muted">Due date</span>
            <span className="fw-semi-bold">{formatDate(due)}</span>
          </div>
          <div className="d-flex justify-content-between mb-2">
            <span className="text-muted">Ordered</span>
            <span className="fw-bold">{fmt(totals.ordered)} m</span>
          </div>
          <div className="d-flex justify-content-between mb-2">
            <span className="text-muted">Allocated</span>
            <span className="fw-semi-bold">{fmt(totals.allocated)} m</span>
          </div>
          <div className="d-flex justify-content-between mb-2">
            <span className="text-muted">Produced</span>
            <span className="fw-semi-bold">{fmt(totals.produced)} m</span>
          </div>
          <div className="d-flex justify-content-between">
            <span className="text-muted">Remaining</span>
            <span className="fw-semi-bold">{fmt(totals.remaining)} m</span>
          </div>
        </div>

        <div className="d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Progress</h4>
          <h4 className="mb-0">{progress}%</h4>
        </div>
      </Card.Body>
    </Card>
  );
};

export default OrderDetailsSummaryCard;
